/**
 * RESPONSIBILITY: the users table — approve, reject, assign roles.
 *
 * Embeddable: the AdminScreen wraps it in a page, and the account bar shows it in a
 * dialog. It renders and mutates users, and knows nothing about where it sits.
 *
 * Every URL comes from FRAMEWORK_ROUTES. A component that builds its own paths is a component
 * that silently 404s the day a route moves.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FRAMEWORK_ROUTES, type AdminUserDto, type AdminUsersResponse } from '@framework/core';
import { get, post } from '../http/client';
import { adminUsersKey } from '../data/cache-keys';

/**
 * The wire contract's type, re-exported under the name this screen has always used.
 *
 * It used to be an interface declared here, structurally similar to AdminUserDto and
 * accountable to nothing. Declaring a response shape next to the code that consumes
 * it is how the two sides drifted in the first place.
 */
export type AdminUser = AdminUserDto;

export function AdminUsers() {
  const qc = useQueryClient();
  const { data, isLoading, isFetching, error } = useQuery<AdminUsersResponse>({
    queryKey: adminUsersKey,
    // get() throws a typed ApiFailure on a non-2xx. The bare fetch this replaced
    // called .json() unconditionally, so a 403 body — {error: 'FORBIDDEN'} — became
    // `data`, passed the truthiness guard below, and crashed on the first field.
    queryFn: () => get<AdminUsersResponse>(FRAMEWORK_ROUTES.adminUsers),
    // Identity data, and never persisted to disk. Serving it stale is the one thing
    // this screen must not do: the global default is CACHE_TTL_MS, which would also
    // stop refetchOnWindowFocus doing anything for half an hour.
    staleTime: 0,
  });

  /** Unsaved role edits, per user. Absent ⇒ show what the server last told us. */
  const [draft, setDraft] = useState<Record<string, string[]>>({});

  if (isLoading) return <p>Loading…</p>;
  if (error || !data) return <p className="ctl-error">Could not load users. {String(error ?? '')}</p>;

  const rolesFor = (u: AdminUser) => draft[u.id] ?? u.roles;

  const toggle = (u: AdminUser, role: string) => {
    const cur = rolesFor(u);
    setDraft({
      ...draft,
      [u.id]: cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role],
    });
  };

  const refresh = () => qc.invalidateQueries({ queryKey: adminUsersKey });

  /**
   * Drop the local edit once the server has it. Kept, it would keep winning over the
   * server's answer in rolesFor() — leaving "Save roles" enabled for a change that
   * was already saved, and showing the draft even if the server stored something
   * else.
   */
  const committed = (id: string) =>
    setDraft(({ [id]: _gone, ...rest }) => rest);

  async function decide(u: AdminUser, action: 'approve' | 'reject') {
    await post(FRAMEWORK_ROUTES.adminDecide(u.id), { action, roles: rolesFor(u) });
    committed(u.id);
    await refresh();
  }

  async function saveRoles(u: AdminUser) {
    await post(FRAMEWORK_ROUTES.adminRoles(u.id), { roles: rolesFor(u) });
    committed(u.id);
    await refresh();
  }

  const pending = data.users.filter((u) => u.status === 'pending');
  const rest = data.users.filter((u) => u.status !== 'pending');

  const row = (u: AdminUser) => (
    <div className={`user-row status-${u.status}`} key={u.id}>
      <div className="user-id">
        <strong>{u.firstName} {u.lastName}</strong>
        <span>@{u.username} · {u.email}</span>
        <span className={`status-chip status-${u.status}`}>{u.status}</span>
        {!u.canLogin && <span className="status-chip status-nologin">cannot log in</span>}
      </div>

      <div className="user-roles">
        {data.roles.map((r) => (
          <label key={r} className={rolesFor(u).includes(r) ? 'on' : ''}>
            <input type="checkbox" checked={rolesFor(u).includes(r)} onChange={() => toggle(u, r)} />
            {r}
          </label>
        ))}
      </div>

      <div className="user-actions">
        {u.status === 'pending' ? (
          <>
            <button className="primary" onClick={() => void decide(u, 'approve')}>Approve</button>
            <button onClick={() => void decide(u, 'reject')}>Reject</button>
          </>
        ) : (
          <button onClick={() => void saveRoles(u)} disabled={!draft[u.id]}>Save roles</button>
        )}
      </div>
    </div>
  );

  return (
    <div className="admin">
      <div className="admin-bar">
        <h3>Pending registrations {pending.length > 0 && <em>({pending.length})</em>}</h3>
        {/*
          Explicit, because nothing else here is live. There is no polling on purpose:
          this list changes when a person clicks something, so a timer would spend a
          request every few seconds to discover nothing, per open tab.
        */}
        <button onClick={() => void refresh()} disabled={isFetching}>
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      {pending.length === 0 ? <p className="ctl-hint">Nothing waiting.</p> : pending.map(row)}
      <h3>Users</h3>
      {rest.map(row)}
    </div>
  );
}
